import React from 'react';
import { Box, Stack } from '@mui/material';
import { useParams } from 'react-router-dom';

import { Page, snackbar, useAppLayoutContextConsumer } from '@occc-app/design-system';
import {
  Button,
  EditIcon,
  DeleteForeverOutlinedIcon,
  IconButton,
  TableContainer,
  TableHeader,
  SearchForm,
  SearchFormProps,
  Tab,
  Tabs,
} from '@occc/ui-design-system';
import { LocationFilter } from '@occc/graphql';
import { useGetLocationGroup } from '@occc-app/dataAccessLayer/locationGroup/queries';
import { useRemoveLocationsFromGroupMutation } from '@occc-app/dataAccessLayer/locationGroup/mutations';
import { useBulkSelect } from '@occc-app/routes/Location/useBulkSelect';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { useTabQueryParam } from '@occc/utils-query-params';

import { LocationGroupTab } from './types';
import { EmptyLocationGroup } from './EmptyLocationGroup';
import { LocationGroupUpdateDialog } from './LocationGroupUpdateDialog';
import { DeleteLocationsFromGroupConfirmationDialog } from './DeleteLocationsFromGroupConfirmationDialog';
import { getBreadcrumbLinks } from './utils';
import {
  ISSUES_AFTER_PARAM,
  ISSUES_BEFORE_PARAM,
  ISSUES_PARAM_NAMES,
  LOCATIONS_AFTER_PARAM,
  LOCATIONS_BEFORE_PARAM,
  LOCATIONS_PARAM_NAMES,
  DEFAULT_LOCATION_GROUP_TAB,
  LOCATION_GROUP_TAB_PARAM,
  LOCATION_GROUP_TABS,
} from './constants';

import { LocationListContainerProps } from '../LocationListPage/SiteManagement/LocationsList/types';
import { LocationListContainer } from '../LocationListPage/SiteManagement/LocationsList/LocationListContainer';
const PAGE_TITLE = 'Location Group';

const transitionStyle = 'transform 500ms cubic-bezier(.75, 0, .25, 1)';
const targetingBoxStyles = {
  transition: transitionStyle,
  overflow: 'hidden',
  width: '100%',
};

const targetingCommonStyles = (isActive: boolean) => ({
  transition: transitionStyle,
  width: '100%',
  overflow: 'hidden',
  maxHeight: isActive ? '100%' : '0px',
});

const leftTabStyles = (isActive: boolean) => ({
  ...targetingCommonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(-100%, 0)',
});

const rightTabStyles = (isActive: boolean) => ({
  ...targetingCommonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(100%, 0)',
});

export const tabLabelsMap: Record<LocationGroupTab, string> = {
  locations: 'All Locations',
  issues: 'Issues',
};

export const LocationGroupDetailsPage: React.FC = () => {
  const { id: locationGroupId = '' } = useParams<{ id: string }>();
  const isUserPermittedToEditGroup = useIsUserPermittedTo(Feature.LOCATION_GROUP_EDIT);

  const [isEdit, setIsEdit] = React.useState<boolean>(false);
  const [isConfirmation, setIsConfirmation] = React.useState<boolean>(false);
  const [activeTab, setActiveTab] = useTabQueryParam<LocationGroupTab>(
    LOCATION_GROUP_TAB_PARAM,
    DEFAULT_LOCATION_GROUP_TAB,
    LOCATION_GROUP_TABS,
  );

  // Search params
  const [locationsSearch, setLocationsSearch] = useSearchParams(LOCATIONS_PARAM_NAMES);
  const [issuesSearch, setIssuesSearch] = useSearchParams(ISSUES_PARAM_NAMES);

  const { data: locationGroup } = useGetLocationGroup(locationGroupId);

  const locationsTabFilter: LocationFilter = React.useMemo(() => {
    return {
      group: {
        uuidContains: [locationGroupId],
      },
      search: locationsSearch,
    };
  }, [locationGroupId, locationsSearch]);

  const [locationsPaginationInfo, onLocationsPageChange] = usePaginationParams({
    beforeParamName: LOCATIONS_BEFORE_PARAM,
    afterParamName: LOCATIONS_AFTER_PARAM,
  });

  const issuesTabFilter: LocationFilter = React.useMemo(() => {
    return {
      group: {
        uuidContains: [locationGroupId],
      },
      isOnlineEquals: false,
      search: issuesSearch,
    };
  }, [locationGroupId, issuesSearch]);

  const [issuesPaginationInfo, onIssuesPageChange] = usePaginationParams({
    beforeParamName: ISSUES_BEFORE_PARAM,
    afterParamName: ISSUES_AFTER_PARAM,
  });

  const openConfirmationDialog = React.useCallback(() => {
    setIsConfirmation(true);
  }, []);

  const onCloseConfirmationDialog = React.useCallback(() => {
    setIsConfirmation(false);
  }, []);

  const { isBulkSelected, bulkSelectedItems, handleBulkSelectAllClick, handleBulkSelect } =
    useBulkSelect();

  const onRemoveLocationsFromGroupCompleted = () => {
    handleBulkSelectAllClick([]);
    onCloseConfirmationDialog();
    snackbar.success('Location(s) were removed from the Location Group.');
  };
  const onRemoveLocationsFromGroupError = () => {
    snackbar.error('Unable to remove Location(s) from the Location Group. Please try again later.');
  };

  const [removeLocationsFromGroup, { loading: removeLocationsLoading }] =
    useRemoveLocationsFromGroupMutation({
      onCompleted: onRemoveLocationsFromGroupCompleted,
      onError: onRemoveLocationsFromGroupError,
      refetchQueries: ['siteManagementLocations'],
    });

  const onDeleteConfirmed = () => {
    removeLocationsFromGroup({
      variables: {
        input: {
          locationGroupUuid: locationGroupId,
          locationUuids: bulkSelectedItems,
        },
      },
    });
  };

  const handleTabChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: LocationGroupTab,
  ) => {
    setActiveTab(value);
  };

  const locationListProps: Omit<LocationListContainerProps, 'filter'> = {
    isBulkSelected,
    bulkSelectedItems,
    handleBulkSelectAllClick,
    handleBulkSelect,
    isUserPermittedToEdit: isUserPermittedToEditGroup,
    getAdditionalRowMenuOptions: isUserPermittedToEditGroup
      ? (location) => [
          {
            label: 'Remove from Group',
            onClick: () => {
              handleBulkSelectAllClick([location.uuid]);
              openConfirmationDialog();
            },
          },
        ]
      : undefined,
    loading: removeLocationsLoading,
  };

  const openEditDialog = React.useCallback(() => {
    setIsEdit(true);
  }, []);

  const onCloseEditDialog = React.useCallback(() => {
    setIsEdit(false);
  }, []);

  const breadcrumbLinks = React.useMemo(() => getBreadcrumbLinks(locationGroup), [locationGroup]);
  useAppLayoutContextConsumer(breadcrumbLinks);

  const renderEmpty = () => {
    return <EmptyLocationGroup />;
  };

  const initSearch: Record<LocationGroupTab, string | undefined> = {
    locations: locationsSearch,
    issues: issuesSearch,
  };

  const onSearchSubmit: SearchFormProps['onSubmit'] = ({ search: value }) => {
    if (activeTab === 'locations') {
      setLocationsSearch(value);
    } else if (activeTab === 'issues') {
      setIssuesSearch(value);
    }
  };

  const headerActions = () => {
    return (
      <Stack direction='row' alignItems='center' gap={1}>
        <SearchForm onSubmit={onSearchSubmit} initialValues={{ search: initSearch[activeTab] }} />
      </Stack>
    );
  };

  const renderContent = () => {
    return (
      <TableContainer>
        <TableHeader
          actions={headerActions()}
          disableTitleTypography
          tabs={
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab value={'locations' satisfies LocationGroupTab} label={tabLabelsMap.locations} />
              <Tab value={'issues' satisfies LocationGroupTab} label={tabLabelsMap.issues} />
            </Tabs>
          }
          bulkSelected={bulkSelectedItems.length}
          bulkActions={
            <IconButton onClick={openConfirmationDialog}>
              <DeleteForeverOutlinedIcon />
            </IconButton>
          }
        />
        <Box style={targetingBoxStyles}>
          <Box
            id={'locations' satisfies LocationGroupTab}
            style={leftTabStyles(activeTab === 'locations')}
          >
            <LocationListContainer
              {...locationListProps}
              filter={locationsTabFilter}
              paginationInfo={locationsPaginationInfo}
              onPageChange={onLocationsPageChange}
              renderEmpty={renderEmpty}
            />
          </Box>
          <Box
            id={'issues' satisfies LocationGroupTab}
            style={rightTabStyles(activeTab === 'issues')}
          >
            <LocationListContainer
              {...locationListProps}
              filter={issuesTabFilter}
              paginationInfo={issuesPaginationInfo}
              onPageChange={onIssuesPageChange}
            />
          </Box>
        </Box>
      </TableContainer>
    );
  };

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      pageHeaderProps={{ title: PAGE_TITLE }}
      actions={
        isUserPermittedToEditGroup && (
          <Button
            color='primary'
            variant='contained'
            startIcon={<EditIcon />}
            role='button'
            onClick={openEditDialog}
          >
            Edit Group
          </Button>
        )
      }
    >
      {renderContent()}

      {isEdit && locationGroup && (
        <LocationGroupUpdateDialog
          locationGroup={locationGroup}
          open={true}
          onClose={onCloseEditDialog}
        />
      )}

      {isConfirmation && (
        <DeleteLocationsFromGroupConfirmationDialog
          onClose={onCloseConfirmationDialog}
          onConfirm={onDeleteConfirmed}
          loading={removeLocationsLoading}
        />
      )}
    </Page>
  );
};
